/**
 * Frontend Error Tracking
 * Captures client-side errors and sends to backend
 */

/**
 * Initialize error tracking
 */
export function initErrorTracking(): void {
  // Global error handler
  window.onerror = (message, source, lineno, colno, error) => {
    sendError({
      type: 'javascript',
      message: String(message),
      source: source || undefined,
      lineno: lineno || undefined,
      colno: colno || undefined,
      stack: error?.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    });
  };

  // Unhandled promise rejection handler
  window.addEventListener('unhandledrejection', (event) => {
    sendError({
      type: 'unhandledrejection',
      reason: event.reason?.toString() || 'Unknown error',
      stack: event.reason?.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    });
  });

  console.log('[ErrorTracking] Initialized');
}

/**
 * Send error to backend
 */
async function sendError(error: {
  type: string;
  message?: string;
  source?: string;
  lineno?: number;
  colno?: number;
  stack?: string;
  reason?: string;
  url?: string;
  userAgent?: string;
  timestamp: string;
}): Promise<void> {
  try {
    // Get current user ID if available
    const userId = getCurrentUserId();
    const featureFlags = getActiveFeatureFlags();

    await fetch('/api/analytics/error', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...error,
        userId,
        featureFlags,
      }),
    });
  } catch (fetchError) {
    // Silently fail - don't break the app
    console.error('[ErrorTracking] Failed to send error:', fetchError);
  }
}

/**
 * Get current user ID (from Firebase auth or localStorage)
 */
function getCurrentUserId(): string | undefined {
  // Try to get from Firebase auth
  if (typeof window !== 'undefined' && (window as any).firebaseAuth) {
    const user = (window as any).firebaseAuth.currentUser;
    if (user?.uid) {
      return user.uid;
    }
  }

  // Fallback to localStorage
  const stored = localStorage.getItem('ozk_user_id');
  return stored || undefined;
}

/**
 * Get active feature flags (from localStorage or context)
 */
function getActiveFeatureFlags(): Record<string, any> {
  try {
    const stored = localStorage.getItem('ozk_feature_flags');
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

/**
 * Manually capture error
 */
export function captureError(error: Error, context?: Record<string, any>): void {
  sendError({
    type: 'manual',
    message: error.message,
    stack: error.stack,
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    ...context,
  });
}

